import { Descriptions, Table, TableColumnsType } from 'antd';
import moment from 'moment';
import QueryString from 'qs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import {
  cancelEventRegister,
  createEventAttendance,
  getEventInfo,
  getEventRegister,
} from '../../../api/events.api';
import Button from '../../../components/Button';
import Checkbox from '../../../components/Checkbox';
import DownloadButton from '../../../components/DownloadButton';
import ErrorResponse from '../../../components/ErrorResponse';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from '../../../components/Loader';
import Pagination from '../../../components/Pagination';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import useNotification from '../../../hooks/useNotification';
import config from '../../../lib/config';
import { getCommonCodeLabel } from '../../../lib/store';
import {
  IEventRegister,
  IGetEventInfo,
  IGetEventRegister,
} from '../../../models/events.model';

interface IMatch {
  eventNo: string;
}

const SessionRegister = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const match = useRouteMatch<IMatch>();
  const eventNo = Number(match.params.eventNo);
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { infoNotification, successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetEventInfo>();
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetEventRegister>();
  const {
    data: submitRes,
    isLoading: submitIsLoading,
    error: submitError,
    execute: submitExecute,
  } = useAxios();
  const {
    data: cancelRes,
    isLoading: cancelIsLoading,
    error: cancelError,
    execute: cancelExecute,
  } = useAxios();

  const [page, setPage] = useState(1);
  const [param, setParam] = useState({});
  const [requireName, setRequireName] = useState('');
  const [mobilePhone, setMobilePhone] = useState('');

  const [isCancelSelectAll, setIsCancelSelectAll] = useState(false);
  const [cancelSelects, setCancelSelects] = useState<number[]>([]);

  useEffect(() => {
    doGetEventRegister();
  }, [location]);

  useEffect(() => {
    if (cancelRes && !cancelIsLoading) {
      doGetEventRegister();
      successNotification(t('common.message.cancel'));
    }
  }, [cancelRes, cancelIsLoading]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      doGetEventRegister();
      successNotification(t('common.message.save1'));
    }
  }, [submitRes, submitIsLoading]);

  const doGetEventRegister = () => {
    execute(getEventInfo('FR', eventNo));

    const _param: any = {
      pageNo: page,
      perPage: config.pageSize,
    };

    if (query.page) {
      const _page = Number(query.page);
      setPage(_page);
      _param.page = _page;
    }
    if (query.requireName) {
      const _requireName = String(query.requireName);
      setRequireName(_requireName);
      _param.requireName = _requireName;
    }
    if (query.mobilePhone) {
      const _mobilePhone = String(query.mobilePhone);
      setMobilePhone(_mobilePhone);
      _param.mobilePhone = _mobilePhone;
    }

    execute2(getEventRegister('FR', eventNo, _param));
    _param.page = _param.pageNo;
    delete _param.pageNo;
    delete _param.perPage;
    setParam(_param);
  };

  const onSetCancelSelects = (eventRequireCode: number) => {
    if (cancelSelects.includes(eventRequireCode)) {
      setCancelSelects(
        cancelSelects.filter((item) => item !== eventRequireCode),
      );
      setIsCancelSelectAll(false);
    } else {
      setCancelSelects([...cancelSelects, eventRequireCode]);
    }
  };

  const onSetCancelSelectAll = () => {
    if (isCancelSelectAll) {
      setCancelSelects([]);
      setIsCancelSelectAll(false);
    } else {
      const _selects: number[] = [];
      res2?.data?.forEach((item) => {
        if (item.cancelYn === 'N') {
          _selects.push(item.eventRequireCode);
        }
      });
      setCancelSelects(_selects);
      setIsCancelSelectAll(true);
    }
  };

  const onSearch = () => {
    const param: any = { page: 1 };

    if (requireName) param.requireName = requireName;
    if (mobilePhone) param.mobilePhone = mobilePhone;

    history.push(
      `/business/session/${eventNo}/register?${QueryString.stringify(param)}`,
    );
  };

  const onCancel = () => {
    if (!cancelSelects.length) {
      return infoNotification(t('business.session.message1'));
    }

    const req: any = [];
    cancelSelects.forEach((item) => {
      req.push({ eventRequireCode: item });
    });

    cancelExecute(cancelEventRegister(eventNo, req));
  };

  const onSubmit = (eventRequireCode: number) => {
    if (!res?.data?.eventDate || !res?.data?.eventTime) return;

    const timeLimit = moment(
      `${res.data.eventDate}T${res.data.eventTime.replace(':', '')}`,
    ).add(10, 'minutes');
    submitExecute(
      createEventAttendance(eventRequireCode, {
        attendStatus: moment().isAfter(timeLimit) ? 'LT' : 'AT',
      }),
    );
  };

  const columns: TableColumnsType<IEventRegister> = [
    {
      title: t('business.inquiry.column2'),
      dataIndex: 'name',
      key: 'name',
      align: 'center',
    },
    {
      title: t('business.inquiry.column3'),
      dataIndex: 'mobilePhone',
      key: 'mobilePhone',
      align: 'center',
    },
    {
      title: t('business.session.column14'),
      dataIndex: 'eventRequireCode',
      key: 'eventRequireCode',
      align: 'center',
      render: (value, record) => {
        return record.attendStatus ? (
          getCommonCodeLabel('BR0008', record.attendStatus, 'ko')
        ) : (
          <Button
            onClick={() => onSubmit(value)}
            className="btn btn-sm btn-success btn-w70p"
            text={t('business.session.button2')}
          />
        );
      },
    },
    {
      title: (
        <>
          {t('business.session.column15')}{' '}
          <Checkbox
            checked={isCancelSelectAll}
            onChange={() => {
              onSetCancelSelectAll();
            }}
            text=""
          />
        </>
      ),
      dataIndex: 'eventRequireCode',
      key: 'eventRequireCode',
      align: 'center',
      render: (value, record) => {
        let checked = false;
        if (
          (cancelSelects.includes(value) || isCancelSelectAll) &&
          record.cancelYn === 'N'
        )
          checked = true;

        return (
          <Checkbox
            checked={checked}
            onChange={() => onSetCancelSelects(value)}
            text=""
            disabled={record.cancelYn === 'Y'}
          />
        );
      },
    },
    {
      title: t('business.session.column16'),
      dataIndex: 'regDate',
      key: 'regDate',
      align: 'center',
      render: (value) => moment(value).format('YYYY.MM.DD HH:mm'),
    },
    {
      title: t('business.session.column17'),
      dataIndex: 'attendSaveDate',
      key: 'attendSaveDate',
      align: 'center',
      render: (value) =>
        value ? moment(value).format('YYYY.MM.DD HH:mm') : '',
    },
  ];

  return (
    <div className="content scrollbar">
      {(isLoading || isLoading2 || submitIsLoading || cancelIsLoading) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={submitError} />
      <ErrorResponse error={cancelError} />
      <SimpleHeader
        iconFile="icon-business-big.svg"
        parentMenu={t('common.menu.business')}
        currentMenu={t('business.session.title2')}
      />

      
      <div className='comm-table__area rowType'>
        <Descriptions
          bordered
          column={3}
          size="small"
          labelStyle={{ width: '16.6%' }}
          contentStyle={{ width: '16.6%' }}
        >
          <Descriptions.Item label={t('common.column.title')} span={3}>
            {res?.data?.eventName}
          </Descriptions.Item>
          <Descriptions.Item label={t('business.session.column18')}>
            {res?.data?.requireCount}
          </Descriptions.Item>
          <Descriptions.Item label={t('business.session.column19')}>
            {res?.data?.paymentCount}
          </Descriptions.Item>
          <Descriptions.Item label={t('business.session.column20')}>
            {res?.data?.attendanceRate}
          </Descriptions.Item>
          <Descriptions.Item label={t('business.session.column21')}>
            {res?.data?.normal}
          </Descriptions.Item>
          <Descriptions.Item label={t('business.session.column22')}>
            {res?.data?.late}
          </Descriptions.Item>
          <Descriptions.Item label={t('business.session.column23')}>
            {res?.data?.absent}
          </Descriptions.Item>
        </Descriptions>
      </div>

      <div className='comm-bx__space'>
        <div className="flex flex-justify-end comm-gap">
          <input
            type="text"
            className="form-control w-150p"
            placeholder={t('business.inquiry.column2')}
            value={requireName}
            onChange={(e) => setRequireName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') onSearch();
            }}
          />
          <input
            type="text"
            className="form-control w-150p"
            placeholder={t('business.inquiry.column3')}
            value={mobilePhone}
            onChange={(e) => setMobilePhone(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') onSearch();
            }}
          />
          <div className='comm-btn__area'>
            <Button
              className="btn-basic small black"
              onClick={onSearch}
              text={t('common.button.search')}
            />
          </div>
        </div>
      </div>
      <div className="comm-table__area">
        <Table
          columns={columns}
          dataSource={res2?.data}
          pagination={{
            current: page,
            pageSize: config.pageSize,
          }}
          rowKey="eventRequireCode"
        />
      </div>
      <div className="row mt-3">
        <div className="col-4 comm-btn__area">
          <DownloadButton onClick={() => {}} />
          <Button
            onClick={() => history.push('/business/session')}
            className="btn-basic gray-line"
            text={t('common.button.list')}
          />
          <Button
            onClick={() => {}}
            className="btn-basic pink-line"
            text={t('business.session.button3')}
          />
        </div>
        <div className="col-4">
          <Pagination
            count={res2?.total || 0}
            page={page}
            param={param}
            path={`/business/session/${eventNo}/register`}
          />
        </div>
        <div className="col-4 comm-btn__area right">
          <Button
            onClick={onCancel}
            className="btn-basic gray"
            text={t('common.button.cancel')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>
    </div>
  );
};

export default SessionRegister;
